import { utils as XLSXUtils } from 'xlsx'
import { DateTime } from 'luxon'

// Builds the client-specified attendance report workbook (see
// "Reportes Nueva App.xlsx" spec): two sheets, headers on ROW 2 (row 1 empty),
// dates as DD.MM.YYYY strings, Spanish weekday, and times as Excel day-fraction
// numbers formatted h:mm:ss.

const TIME_FORMAT = 'h:mm:ss'

const toDdMmYyyy = (isoDate) => DateTime.fromISO(isoDate).toFormat('dd.MM.yyyy')

const spanishWeekday = (isoDate) => {
  const day = DateTime.fromISO(isoDate).setLocale('es').toFormat('cccc')
  return day.charAt(0).toUpperCase() + day.slice(1)
}

// 'HH:MM' (or 'HH:MM:SS') -> Excel time fraction (portion of a day), or null.
const fractionFromTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null
  const [h = 0, m = 0, s = 0] = timeStr.split(':').map(Number)
  if ([h, m, s].some(Number.isNaN)) return null
  return (h * 3600 + m * 60 + s) / 86400
}

// ISO datetime -> Excel time fraction in farm-local time, or null.
const fractionFromIso = (iso) => {
  if (!iso) return null
  const dt = DateTime.fromISO(iso).setZone('America/Mexico_City')
  if (!dt.isValid) return null
  return (dt.hour * 3600 + dt.minute * 60 + dt.second) / 86400
}

// Worker code: the legacy numeric id when available, else the internal id.
const workerCode = (row) => {
  if (row.oldId !== null && row.oldId !== undefined && String(row.oldId).trim() !== '') {
    const old = Number(row.oldId)
    return Number.isFinite(old) ? old : row.oldId
  }
  return row.workerId
}

// Apply a number format to every cell of the given 0-based columns, from
// dataStartRow (0-based) to the sheet's last row.
const applyColumnFormats = (worksheet, columns, format, dataStartRow) => {
  const range = XLSXUtils.decode_range(worksheet['!ref'])
  for (let r = dataStartRow; r <= range.e.r; r++) {
    for (const c of columns) {
      const cell = worksheet[XLSXUtils.encode_cell({ r, c })]
      if (cell && cell.t === 'n') cell.z = format
    }
  }
}

const buildAttendanceSheet = (workersTimeline, filterDate) => {
  const fecha = toDdMmYyyy(filterDate)
  const dia = spanishWeekday(filterDate)
  const rows = (workersTimeline || []).map(worker => ([
    workerCode(worker),
    worker.workerName || '',
    worker.groupName || '',
    fecha,
    dia,
    fractionFromTimeString(worker.entryTime)
  ]))

  const worksheet = XLSXUtils.aoa_to_sheet([
    [],
    ['Código de trabajador', 'Nombre', 'Nombre de grupo', 'Fecha', 'Día semana', 'Hora inicio jornada'],
    ...rows
  ])
  applyColumnFormats(worksheet, [5], TIME_FORMAT, 2)
  worksheet['!cols'] = [{ wch: 20 }, { wch: 24 }, { wch: 17 }, { wch: 15 }, { wch: 13 }, { wch: 19 }]
  return worksheet
}

const buildMonitorSheet = (workersMonitor, filterDate) => {
  const fecha = toDdMmYyyy(filterDate)
  const dia = spanishWeekday(filterDate)
  const rows = (workersMonitor || []).map(row => ([
    workerCode(row),
    row.workerName || '',
    row.groupName || '',
    row.activity || '',
    row.blockName || '',
    Number.isFinite(row.tunnel) ? row.tunnel : '',
    Number.isFinite(row.line) ? row.line : '',
    fractionFromIso(row.firstScan),
    fractionFromIso(row.lastScan),
    Number(row.totalPlants) || 0,
    Number(row.totalHours) || 0,
    Number(row.plantsPerHour) || 0,
    fecha,
    dia
  ]))

  const worksheet = XLSXUtils.aoa_to_sheet([
    [],
    ['Código', 'Nombre', 'Nombre de grupo', 'Actividad', 'Invernadero', 'Tunel', 'Línea',
      'Primer Escaneo', 'Último Escaneo', 'Unidades', 'Horas', 'Promedio por hora', 'Fecha', 'Día'],
    ...rows
  ])
  applyColumnFormats(worksheet, [7, 8], TIME_FORMAT, 2)
  applyColumnFormats(worksheet, [9], '#,##0', 2)
  applyColumnFormats(worksheet, [10], '#,##0.00', 2)
  worksheet['!cols'] = [
    { wch: 12 }, { wch: 24 }, { wch: 19 }, { wch: 19 }, { wch: 13 }, { wch: 12 }, { wch: 12 },
    { wch: 21 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 12 }
  ]
  return worksheet
}

export const buildAttendanceReportWorkbook = ({ workersTimeline, workersMonitor, filterDate }) => {
  const workbook = XLSXUtils.book_new()
  XLSXUtils.book_append_sheet(workbook, buildAttendanceSheet(workersTimeline, filterDate), 'Reporte de asistencia')
  XLSXUtils.book_append_sheet(workbook, buildMonitorSheet(workersMonitor, filterDate), 'Monitoreo de trabajadores')
  return workbook
}
